/**
 * DevicesPage — fleet device management. Lists every tablet the telemetry
 * service has seen (and any with an active session), annotated with the driver
 * + vehicle it is bound to and the trip that vehicle is running. Unbound /
 * unassigned devices are surfaced too, so dispatch can spot a tablet that is
 * online but not signed into any bus.
 *
 * Model/OS/app-version/heartbeat come from an external telemetry service
 * (device-telemetry-service) the backend proxies; when that upstream isn't
 * reachable those fields read "—" rather than being invented client-side.
 * Status/last-seen fall back to the device_session's own last_seen_at so the
 * page still means something without the telemetry integration running.
 */
import { Fragment, useCallback, useMemo, useState } from "react";
import { api } from "../api/client";
import { usePollingQuery } from "../hooks/usePollingQuery";
import type { DeviceView, Vehicle } from "../api/types";

function isOnline(d: DeviceView): boolean {
  return (d.status || "").toUpperCase() === "ONLINE";
}

function fmtAgo(epoch?: number): string {
  if (!epoch) return "—";
  // Telemetry may report seconds or millis; normalise to millis.
  const ms = epoch > 1e12 ? epoch : epoch * 1000;
  const diff = Date.now() - ms;
  if (diff < 0) return "now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtDelay(seconds?: number): { text: string; tone: string } {
  if (seconds == null) return { text: "—", tone: "info" };
  const abs = Math.abs(seconds);
  const sign = seconds >= 0 ? "+" : "−";
  const tone = abs > 300 ? "crit" : abs > 60 ? "warn" : "ok";
  return { text: abs < 60 ? `${sign}${abs}s` : `${sign}${Math.round(abs / 60)}m`, tone };
}

/** Row-level status: "online" (connected + signed in), "idle" (connected,
 *  nobody signed in), "offline" (not reachable) — all derived from real
 *  fields (status + driver_name), nothing invented. */
function rowStatus(d: DeviceView): { label: string; tone: "ok" | "info" | "muted" } {
  if (!isOnline(d)) return { label: "Offline", tone: "muted" };
  if (!d.driver_name) return { label: "Idle", tone: "info" };
  return { label: "Online", tone: "ok" };
}

/**
 * The driver cell. A tablet whose session has gone three hours without a
 * working sign-in is still bound to its bus but names no driver
 * (`signed_out`); a bare "—" there looked like a missing value, not a fact.
 */
function driverCell(d: DeviceView) {
  if (d.driver_name) return d.driver_name;
  if (d.bound && d.signed_out) {
    return (
      <span className="devices-signed-out" title="Bound to this bus, but no working sign-in for over 3 hours">
        Signed out
      </span>
    );
  }
  return <span className="secondary-text">—</span>;
}

export default function DevicesPage() {
  const q = usePollingQuery<DeviceView[]>(
    () => api.listDevices().then((r) => r.devices ?? []),
    [],
    { intervalMs: 5000 }
  );
  const devices = q.data ?? [];
  const [acting, setActing] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Vehicle roster for the per-device assignment dropdown. Refreshed lazily —
  // the fleet list changes far slower than the device heartbeats.
  const vehiclesQuery = usePollingQuery<Vehicle[]>(
    () => api.listVehicles().then((r) => (Array.isArray(r) ? r : r.data ?? [])),
    [],
    { intervalMs: 60000 }
  );
  const vehicles = vehiclesQuery.data ?? [];

  const assignVehicle = useCallback(async (deviceId: string, vehicleId: string) => {
    setActing(deviceId);
    try {
      await api.assignDeviceVehicle(deviceId, vehicleId);
      setToast(vehicleId ? `Assigned vehicle → ${deviceId}` : `Cleared vehicle for ${deviceId}`);
    } catch (e) {
      setToast(e instanceof Error ? `Error: ${e.message}` : "Assign failed");
    } finally {
      setActing(null);
    }
  }, []);

  // Register a new vehicle so it can be assigned to a tablet. The fleet has no
  // other create path (the API stub is now implemented). vehicle_id = fleet
  // code, plate = what operators see.
  const [vCode, setVCode] = useState("");
  const [vPlate, setVPlate] = useState("");
  const addVehicle = useCallback(async () => {
    const code = vCode.trim();
    if (!code) return;
    setActing("add-vehicle");
    try {
      await api.createVehicle({
        vehicle_id: code,
        license_plate: vPlate.trim() || undefined,
        vehicle_label: code,
      });
      setToast(`Vehicle ${code} added`);
      setVCode("");
      setVPlate("");
      await vehiclesQuery.refetch();
    } catch (e) {
      setToast(e instanceof Error ? `Error: ${e.message}` : "Add vehicle failed");
    } finally {
      setActing(null);
    }
  }, [vCode, vPlate, vehiclesQuery]);

  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editPlate, setEditPlate] = useState("");

  const startEditVehicle = useCallback((v: Vehicle) => {
    setEditingVehicle(v);
    setEditLabel(v.vehicle_label || "");
    setEditPlate(v.license_plate || "");
  }, []);

  const saveVehicle = useCallback(async () => {
    if (!editingVehicle) return;
    setActing("edit-vehicle");
    try {
      await api.updateVehicle(editingVehicle.id, {
        vehicle_label: editLabel.trim() || undefined,
        license_plate: editPlate.trim() || undefined,
      });
      setToast(`Vehicle ${editingVehicle.vehicle_id} updated`);
      setEditingVehicle(null);
      await vehiclesQuery.refetch();
    } catch (e) {
      setToast(e instanceof Error ? `Error: ${e.message}` : "Update failed");
    } finally {
      setActing(null);
    }
  }, [editingVehicle, editLabel, editPlate, vehiclesQuery]);

  const deleteVehicle = useCallback(async (v: Vehicle) => {
    if (!window.confirm(`Delete vehicle ${v.vehicle_id} (${v.license_plate || v.vehicle_label || "no plate"})? This cannot be undone.`)) return;
    setActing(v.id);
    try {
      await api.deleteVehicle(v.id);
      setToast(`Vehicle ${v.vehicle_id} deleted`);
      await vehiclesQuery.refetch();
    } catch (e) {
      setToast(e instanceof Error ? `Error: ${e.message}` : "Delete failed");
    } finally {
      setActing(null);
    }
  }, [vehiclesQuery]);

  const summary = useMemo(() => {
    let online = 0;
    for (const d of devices) if (isOnline(d)) online++;
    return { total: devices.length, online, offline: devices.length - online };
  }, [devices]);

  const forceUpdate = useCallback(async (id: string) => {
    if (!window.confirm(`Send force_update to ${id}?`)) return;
    setActing(id);
    try {
      const r = await api.sendDeviceDiagnostics(id, "force_update");
      setToast(`force_update → ${id} (cmd ${r.cmd_id.slice(0, 8)}…)`);
    } catch (e) {
      setToast(e instanceof Error ? `Error: ${e.message}` : "force_update failed");
    } finally {
      setActing(null);
    }
  }, []);

  const sorted = useMemo(() => {
    // Online-unbound first (needs attention), then bound, then offline.
    const rank = (d: DeviceView) => (isOnline(d) && !d.bound ? 0 : d.bound ? 1 : 2);
    return [...devices].sort((a, b) => rank(a) - rank(b) || a.device_id.localeCompare(b.device_id));
  }, [devices]);

  return (
    <div className="page devices-page">
      <div className="devices-header">
        <h1>Devices</h1>
        <p className="secondary-text">Driver console tablets and their session state.</p>
      </div>

      {toast && <div className="banner">{toast}</div>}
      {q.error && <div className="banner banner-error">{q.error}</div>}

      <section className="card devices-sessions-card">
        <div className="devices-sessions-head">
          <h3>
            <span className="devices-bar" />
            Device sessions
            <span className="devices-count-pill ok">{summary.online} ONLINE</span>
            <span className="devices-count-pill muted">{summary.offline} OFFLINE</span>
          </h3>
          <span className="secondary-text">{q.lastUpdatedAt ? `Updated ${fmtAgo(q.lastUpdatedAt)}` : ""}</span>
        </div>

        <div className="devices-table-wrap">
          <table className="devices-table">
            <thead>
              <tr>
                <th>Device</th><th>Vehicle</th><th>Driver</th><th>Status</th><th>Last seen</th><th>Firmware</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 && (
                <tr><td colSpan={6} className="empty">No devices reporting.</td></tr>
              )}
              {sorted.map((d) => {
                const status = rowStatus(d);
                const expanded = expandedId === d.device_id;
                const delay = fmtDelay(d.delay_seconds);
                return (
                  <Fragment key={d.device_id}>
                    <tr
                      className={"devices-row" + (expanded ? " devices-row-selected" : "")}
                      onClick={() => setExpandedId(expanded ? null : d.device_id)}
                    >
                      <td className="mono devices-device-cell">{d.device_id}</td>
                      <td className="mono">{d.vehicle_code || d.vehicle_label || d.vehicle_plate || "—"}</td>
                      <td>{driverCell(d)}</td>
                      <td><span className={`devices-status-pill ${status.tone}`}>{status.label.toUpperCase()}</span></td>
                      <td className="secondary-text">{fmtAgo(d.last_heartbeat_at)}</td>
                      <td className="mono">{d.app_version || "—"}</td>
                    </tr>
                    {expanded && (
                      <tr className="devices-detail-row" key={`${d.device_id}-detail`}>
                        <td colSpan={6}>
                          <div className="devices-detail" onClick={(e) => e.stopPropagation()}>
                            <div className="devices-detail-col">
                              <span className="devices-detail-label">Model / OS</span>
                              <div>{d.device_model || "—"}</div>
                              <div className="secondary-text">{d.os_version || ""}</div>
                            </div>
                            <div className="devices-detail-col">
                              <span className="devices-detail-label">Trip</span>
                              {d.trip_instance_id ? (
                                <div>
                                  {d.route_short_name ? <span className="route-badge">{d.route_short_name}</span> : null}
                                  <span style={{ marginLeft: d.route_short_name ? 6 : 0 }}>
                                    {d.trip_status?.replace("_", " ")}
                                    {d.current_stop_sequence != null ? ` · stop ${d.current_stop_sequence}` : ""}
                                  </span>
                                  <span className={`pill sev-${delay.tone}`} style={{ marginLeft: 6 }}>{delay.text}</span>
                                </div>
                              ) : (
                                <span className="secondary-text">No active trip</span>
                              )}
                            </div>
                            <div className="devices-detail-col">
                              <span className="devices-detail-label">Reassign vehicle</span>
                              <select
                                value={d.vehicle_id ?? ""}
                                disabled={acting === d.device_id}
                                onChange={(e) => assignVehicle(d.device_id, e.target.value)}
                              >
                                <option value="">— unassigned —</option>
                                {vehicles.map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {(v.vehicle_label || v.vehicle_id) + (v.license_plate ? ` · ${v.license_plate}` : "")}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="devices-detail-col devices-detail-actions">
                              <button
                                className="btn ghost"
                                type="button"
                                disabled={acting === d.device_id}
                                onClick={() => forceUpdate(d.device_id)}
                              >
                                {acting === d.device_id ? "…" : "Force update"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <details className="card" style={{ padding: 0 }}>
        <summary style={{ padding: "10px 16px", cursor: "pointer", fontWeight: 600 }}>
          Vehicles ({vehicles.length})
        </summary>
        <div style={{ padding: "0 16px 12px" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <input
              placeholder="Code (e.g. CDA-002)"
              value={vCode}
              onChange={(e) => setVCode(e.target.value)}
              style={{ maxWidth: 150 }}
            />
            <input
              placeholder="Plate (e.g. CDA-2026-002)"
              value={vPlate}
              onChange={(e) => setVPlate(e.target.value)}
              style={{ maxWidth: 170 }}
            />
            <button
              className="btn primary"
              type="button"
              disabled={!vCode.trim() || acting === "add-vehicle"}
              onClick={addVehicle}
            >
              {acting === "add-vehicle" ? "…" : "Add"}
            </button>
          </div>
          <table className="data-table compact">
            <thead>
              <tr><th>Code</th><th>Label</th><th>Plate</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id}>
                  <td className="mono">{v.vehicle_id}</td>
                  <td>{editingVehicle?.id === v.id
                    ? <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} style={{ maxWidth: 120 }} />
                    : (v.vehicle_label || "—")}</td>
                  <td>{editingVehicle?.id === v.id
                    ? <input value={editPlate} onChange={(e) => setEditPlate(e.target.value)} style={{ maxWidth: 140 }} />
                    : (v.license_plate || "—")}</td>
                  <td><span className={`pill sev-${v.status === "active" ? "ok" : "info"}`}>{v.status}</span></td>
                  <td style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    {editingVehicle?.id === v.id ? (
                      <>
                        <button className="btn ghost" type="button" disabled={acting === "edit-vehicle"} onClick={saveVehicle}>
                          {acting === "edit-vehicle" ? "…" : "Save"}
                        </button>
                        <button className="btn ghost" type="button" onClick={() => setEditingVehicle(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="btn ghost" type="button" onClick={() => startEditVehicle(v)}>Edit</button>
                        <button className="btn ghost danger" type="button" disabled={acting === v.id} onClick={() => deleteVehicle(v)}>
                          {acting === v.id ? "…" : "Delete"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
